/**
 * RulesCard.js
 *
 * Displays the team composition and bonus rules for the room's library and settings.
 * Clean two-column layout with left/right alignment for readability.
 */

import React from "react";
import Card, { CardHeader, CardTitle, CardContent } from "../../../components/ui/Card";
import { getCategoryAttributes } from "../library/libraries";
import { getAllDifferentTarget } from "../helpers/scoring";

const RuleRow = ({ label, value }) => (
    <div className="flex justify-between gap-4">
        <span>{label}</span>
        <span className="font-semibold text-yellow whitespace-nowrap">{value}</span>
    </div>
);

const RulesCard = ({ settings, library }) => {
    const rules = Object.entries(settings.attributeRules);
    const categoryAttributes = getCategoryAttributes(library);
    const allDifferentLabel = (name) => {
        const attribute = categoryAttributes.find((a) => a.name === name);
        if (!attribute) return `All different ${name}`;
        const target = getAllDifferentTarget(settings, attribute);
        return target === attribute.values.length ? `Every ${name} (all ${target})` : `All ${target} submitted with different ${name}`;
    };

    const compositionRows = [
        { label: "Items to buy", value: settings.minItemsPerTeam === settings.maxItemsPerTeam ? settings.minItemsPerTeam : `${settings.minItemsPerTeam}–${settings.maxItemsPerTeam}` },
        { label: "Items to submit", value: settings.itemsToSubmit },
        { label: "Bids start at", value: `₹${settings.startingBid}` },
        { label: "Bid step", value: `₹${settings.bidStep}` },
        ...rules.flatMap(([attribute, rule]) => [
            rule.minDifferent > 0 && { label: `Different ${attribute} values in the submission`, value: `at least ${rule.minDifferent}` },
            rule.maxPerValue !== null && rule.maxPerValue !== undefined && { label: `Maximum per ${attribute}`, value: rule.maxPerValue },
        ]).filter(Boolean),
    ];

    const bonusRows = rules.flatMap(([attribute, rule]) => [
        rule.bonusOneValue > 0 && { label: `All submitted share one ${attribute}`, value: `+${rule.bonusOneValue} pts` },
        rule.bonusEveryValue > 0 && { label: allDifferentLabel(attribute), value: `+${rule.bonusEveryValue} pts` },
    ]).filter(Boolean);

    return (
        <Card className="bg-zinc-900 border-zinc-700 shadow-md">
            <CardHeader className="pb-2 text-left">
                <CardTitle className="text-sm text-zinc-400">Rules · {library.name}</CardTitle>
            </CardHeader>

            <CardContent className="text-zinc-300 text-sm leading-relaxed space-y-6 text-left">
                <div className="border-t border-zinc-700 my-0"></div>

                {/* --- Team Composition Section --- */}
                <div>
                    <p className="text-zinc-400 font-semibold mb-2 uppercase text-xs">
                        Team Composition:
                    </p>
                    <div className="space-y-1">
                        {compositionRows.map((row) => <RuleRow key={row.label} {...row} />)}
                    </div>
                </div>

                {/* --- Bonus Points Section --- */}
                {bonusRows.length > 0 && (
                    <>
                        <div className="border-t border-zinc-700 my-2"></div>
                        <div>
                            <p className="text-zinc-400 font-semibold mb-2 uppercase text-xs">
                                Bonus Points:
                            </p>
                            <div className="space-y-1">
                                {bonusRows.map((row) => <RuleRow key={row.label} {...row} />)}
                            </div>
                        </div>
                    </>
                )}

                <p className="italic text-zinc-500 text-xs pt-2">
                    Teams are judged on {library.scoreAttribute}: each team submits {settings.itemsToSubmit} of its items, and its best set (bonuses included) is picked for it.
                </p>
            </CardContent>
        </Card>
    );
};

export default RulesCard;
